/**
 * Comic Data Provider Abstraction Layer
 * 
 * This module provides an abstraction layer for fetching comic book data from various sources.
 * It supports multiple providers (Comic Vine, Marvel, etc.) and can be extended to add more.
 * 
 * Available Providers:
 * 1. Comic Vine API - Comprehensive database covering Marvel, DC, Image, and other publishers
 *    - Requires free API key from https://comicvine.gamespot.com/api/
 * 2. Marvel API (Deprecated) - Original Marvel-only source
 * 3. Mock Provider - For testing without API access
 */

import axios from 'axios';
import config from './config';
import { stripYearFromTitle } from './utils';

// Provider configuration
const PROVIDERS = {
  COMIC_VINE: 'comicvine',
  MARVEL: 'marvel',
  MOCK: 'mock'
};

// Default provider - can be changed via configuration
let currentProvider = PROVIDERS.COMIC_VINE;

/**
 * Set the active comic data provider
 * @param {string} provider - Provider name from PROVIDERS constant
 */
export function setProvider(provider) {
  if (Object.values(PROVIDERS).includes(provider)) {
    currentProvider = provider;
  } else {
    console.warn(`Unknown provider: ${provider}. Using default.`);
  }
}

/**
 * Get the current provider name
 * @returns {string} Current provider name
 */
export function getCurrentProvider() {
  return currentProvider;
}

// ============================================================================
// COMIC VINE API IMPLEMENTATION
// ============================================================================

// Validate API key is loaded
if (!config.COMIC_VINE_API_KEY || config.COMIC_VINE_API_KEY === 'YOUR_COMIC_VINE_API_KEY_HERE') {
  console.warn('Comic Vine API key not found. Please check your configuration.');
} else {
  console.log('Comic Vine API key loaded successfully');
}

// Note about Firebase proxy
console.info('Comic Vine API calls are routed through Firebase Cloud Function to bypass browser CORS restrictions.');
console.info('Firebase proxy URL:', config.FIREBASE_FUNCTION_URL);

const COMIC_VINE_CONFIG = {
  baseUrl: 'https://comicvine.gamespot.com/api',
  // Firebase Cloud Function proxy URL
  firebaseProxyUrl: config.FIREBASE_FUNCTION_URL,
  // External proxies as fallback (if Firebase function is unavailable)
  fallbackProxies: [
    {
      name: 'allorigins.win',
      url: 'https://api.allorigins.win/get?url=',
      needsEncoding: true,
      responseHandler: (response) => {
        if (response.data && response.data.contents) {
          return JSON.parse(response.data.contents);
        }
        return response.data;
      }
    },
    {
      name: 'cors-anywhere',
      url: 'https://cors-anywhere.herokuapp.com/',
      needsEncoding: false,
      responseHandler: (response) => response.data
    }
  ],
  // API key loaded from configuration
  apiKey: config.COMIC_VINE_API_KEY,
  format: 'json'
};

// Helper function to build fallback proxy URL (for external proxies)
const buildFallbackProxyUrl = (endpoint, params, proxyIndex = 0) => {
  const proxyConfig = COMIC_VINE_CONFIG.fallbackProxies[proxyIndex];

  const queryString = Object.keys(params).map(key =>
    `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
  ).join('&');

  const fullUrl = `${COMIC_VINE_CONFIG.baseUrl}${endpoint}?${queryString}`;

  if (proxyConfig.needsEncoding) {
    return `${proxyConfig.url}${encodeURIComponent(fullUrl)}`;
  } else {
    return `${proxyConfig.url}${fullUrl}`;
  }
};

// Helper function to make Comic Vine API requests via Firebase Function
const makeComicVineRequest = async (endpoint, params) => {
  try {
    // First, try Firebase Cloud Function
    console.log(`Making request to Firebase proxy: ${COMIC_VINE_CONFIG.firebaseProxyUrl}`);

    const firebaseResponse = await axios.post(COMIC_VINE_CONFIG.firebaseProxyUrl, {
      endpoint: endpoint,
      params: params
    }, {
      timeout: 30000, // 30 second timeout
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log('Firebase proxy response:', firebaseResponse.data);

    if (firebaseResponse.data.error !== 'OK') {
      throw new Error(firebaseResponse.data.error);
    }

    return { data: firebaseResponse.data };

  } catch (firebaseError) {
    console.warn('Firebase proxy failed, trying fallback proxies:', firebaseError.message);

    // Fallback to external proxies if Firebase function fails
    let response;
    let lastError = firebaseError;

    for (let i = 0; i < COMIC_VINE_CONFIG.fallbackProxies.length; i++) {
      try {
        const proxyConfig = COMIC_VINE_CONFIG.fallbackProxies[i];
        const url = buildFallbackProxyUrl(endpoint, params, i);
        console.log(`Trying fallback proxy ${i} (${proxyConfig.name}): ${url}`);

        const rawResponse = await axios.get(url, { timeout: 15000 });
        const processedData = proxyConfig.responseHandler(rawResponse);

        if (processedData.error !== 'OK') {
          throw new Error(processedData.error);
        }

        response = { data: processedData };
        console.log(`Fallback proxy ${proxyConfig.name} succeeded`);
        break;
      } catch (error) {
        lastError = error;
        console.warn(`Fallback proxy ${i} failed:`, error.message);
      }
    }

    if (!response) {
      throw new Error(`All proxies failed. Firebase error: ${firebaseError.message}. Last fallback error: ${lastError.message}`);
    }

    return response;
  }
};

/**
 * Search for a character using Comic Vine API
 * @param {string} characterName - Name of the character to search
 * @param {string} publisher - Publisher filter (marvel, dc, etc.)
 * @returns {Promise} Character data in normalized format
 */
async function searchCharacterComicVine(characterName, publisher = null) {
  try {
    const params = {
      api_key: COMIC_VINE_CONFIG.apiKey,
      format: COMIC_VINE_CONFIG.format,
      filter: `name:${characterName}`,
      field_list: 'id,name,deck,description,image,publisher,count_of_issue_appearances'
    };

    let response;

    response = await makeComicVineRequest('/characters', params);

    const characters = response.data.results || [];

    // Filter by publisher if specified
    let filteredCharacters = characters;
    if (publisher && publisher.trim() !== 'Unknown' && publisher.trim() !== '') {
      filteredCharacters = characters.filter(char =>
        char.publisher && char.publisher.name.toLowerCase().includes(publisher.toLowerCase())
      );
    }

    // Convert Comic Vine format to our normalized format
    return filteredCharacters.map(char => normalizeCharacter(char, 'comicvine'));
  } catch (error) {
    console.error('Comic Vine API error:', error);
    throw error;
  }
}

/**
 * Generic search function for Comic Vine resources
 * @param {string} endpoint - API endpoint (e.g., '/characters', '/teams', '/story_arcs')
 * @param {string} name - Name to search for
 * @param {string} publisher - Publisher filter (marvel, dc, etc.)
 * @param {string} resourceType - Type identifier for the resource ('character', 'team', 'storyarc')
 * @returns {Promise} Resource data in normalized format
 */
async function searchComicVineResource(endpoint, name, publisher, resourceType) {
  try {
    const params = {
      api_key: COMIC_VINE_CONFIG.apiKey,
      format: COMIC_VINE_CONFIG.format,
      filter: `name:${name}`,
      field_list: 'id,name,deck,description,image,publisher,count_of_issue_appearances'
    };

    const response = await makeComicVineRequest(endpoint, params);

    const results = response.data.results || [];

    // Filter by publisher if specified
    let filteredResults = results;
    if (publisher && publisher.trim() !== 'Unknown' && publisher.trim() !== '') {
      filteredResults = results.filter(item =>
        item.publisher && item.publisher.name.toLowerCase().includes(publisher.toLowerCase())
      );
    }

    // Convert Comic Vine format to our normalized format (reuse normalizeCharacter as structure is similar)
    return filteredResults.map(item => ({
      ...normalizeCharacter(item, 'comicvine'),
      resourceType: resourceType // Add type identifier
    }));
  } catch (error) {
    console.error(`Comic Vine API error (${resourceType}):`, error);
    throw error;
  }
}

/**
 * Search for a team using Comic Vine API
 * @param {string} teamName - Name of the team to search
 * @param {string} publisher - Publisher filter (marvel, dc, etc.)
 * @returns {Promise} Team data in normalized format (similar to character format)
 */
async function searchTeamComicVine(teamName, publisher = null) {
  return searchComicVineResource('/teams', teamName, publisher, 'team');
}

/**
 * Search for a story arc using Comic Vine API
 * @param {string} arcName - Name of the story arc to search
 * @param {string} publisher - Publisher filter (marvel, dc, etc.)
 * @returns {Promise} Story arc data in normalized format (similar to character format)
 */
async function searchStoryArcComicVine(arcName, publisher = null) {
  return searchComicVineResource('/story_arcs', arcName, publisher, 'storyarc');
}

/**
 * Search for comic series using Comic Vine API
 * @param {number} characterId - Character ID (if searching by character)
 * @param {string} titleStartsWith - Series title search
 * @param {number} startYear - Not used for Comic Vine (kept for API compatibility)
 * @param {number} offset - Offset for pagination (default 0)
 * @returns {Promise} Series data in normalized format with pagination info
 */
async function searchSeriesComicVine(characterId = null, titleStartsWith = '', startYear = null, offset = 0) {
  try {
    // Strip year from title for better Comic Vine search matching
    // e.g., "Captain America (1968)" -> "Captain America"
    const cleanTitle = stripYearFromTitle(titleStartsWith);
    
    let filter = '';
    if (cleanTitle) {
      filter += `name:${cleanTitle}`;
    }

    const params = {
      api_key: COMIC_VINE_CONFIG.apiKey,
      format: COMIC_VINE_CONFIG.format,
      filter: filter || undefined,
      field_list: 'id,name,start_year,description,image,count_of_issues,publisher',
      sort: 'name:asc',
      limit: 100,
      offset: offset
    };

    console.log(`Comic Vine volumes search: ${cleanTitle || 'all volumes'}${titleStartsWith !== cleanTitle ? ` (original: "${titleStartsWith}")` : ''} (offset: ${offset})`);
    const response = await makeComicVineRequest('/volumes', params);

    const series = response.data.results || [];
    const totalResults = response.data.number_of_total_results || series.length;
    const hasMore = (offset + series.length) < totalResults;

    return {
      results: series.map(s => normalizeSeries(s, 'comicvine')),
      hasMore: hasMore,
      totalResults: totalResults,
      offset: offset
    };
  } catch (error) {
    console.error('Comic Vine API error:', error);
    throw error;
  }
}

/**
 * Search for recent comic series using date_added filter (alternative to start_year)
 * @param {string} titleStartsWith - Series title search
 * @param {string} dateRange - Date range in format "YYYY-MM-DD|YYYY-MM-DD" (start|end)
 * @param {number} offset - Offset for pagination (default 0)
 * @returns {Promise} Series data in normalized format with pagination info
 */
async function searchRecentSeriesComicVine(titleStartsWith = '', dateRange = null, offset = 0) {
  try {
    // Strip year from title for better Comic Vine search matching
    const cleanTitle = stripYearFromTitle(titleStartsWith);
    
    let filter = '';
    if (cleanTitle) {
      filter += `name:${cleanTitle}`;
    }
    if (dateRange) {
      filter += (filter ? ',' : '') + `date_added:${dateRange}`;
    }

    const params = {
      api_key: COMIC_VINE_CONFIG.apiKey,
      format: COMIC_VINE_CONFIG.format,
      filter: filter || undefined,
      field_list: 'id,name,start_year,description,image,count_of_issues,publisher,date_added',
      sort: 'date_added:desc',
      limit: 100,
      offset: offset
    };

    console.log(`Comic Vine recent volumes search: ${cleanTitle || 'all volumes'}${titleStartsWith !== cleanTitle ? ` (original: "${titleStartsWith}")` : ''}${dateRange ? ` (date range: ${dateRange})` : ''} (offset: ${offset})`);
    const response = await makeComicVineRequest('/volumes', params);

    const series = response.data.results || [];
    const totalResults = response.data.number_of_total_results || series.length;
    const hasMore = (offset + series.length) < totalResults;

    return {
      results: series.map(s => normalizeSeries(s, 'comicvine')),
      hasMore: hasMore,
      totalResults: totalResults,
      offset: offset
    };
  } catch (error) {
    console.error('Comic Vine API error:', error);
    throw error;
  }
}

/**
 * Get comics for a series using Comic Vine API
 * @param {number} seriesId - Series/Volume ID
 * @param {number} limit - Max results to return
 * @param {number} offset - Offset for pagination
 * @returns {Promise} Comics data in normalized format
 */
async function getComicsForSeriesComicVine(seriesId, limit = 99, offset = 0) {
  try {
    const params = {
      api_key: COMIC_VINE_CONFIG.apiKey,
      format: COMIC_VINE_CONFIG.format,
      filter: `volume:${seriesId}`,
      field_list: 'id,name,issue_number,description,image,volume,cover_date',
      sort: 'issue_number:asc',
      limit: limit,
      offset: offset
    };

    const response = await makeComicVineRequest('/issues', params);

    const comics = response.data.results || [];
    return comics.map(comic => normalizeComic(comic, 'comicvine'));
  } catch (error) {
    console.error('Comic Vine API error:', error);
    throw error;
  }
}

// ============================================================================
// MARVEL API IMPLEMENTATION (Legacy - Deprecated)
// ============================================================================

const MARVEL_CONFIG = {
  baseUrl: 'https://gateway.marvel.com:443/v1/public',
  apiKey: '71baad4ef825a96718c5040630c88fe4'
};

async function searchCharacterMarvel(characterName, publisher = null) {
  // Only support Marvel characters with Marvel API
  if (publisher && publisher !== 'marvel') {
    return [];
  }

  const response = await axios.get(`${MARVEL_CONFIG.baseUrl}/characters`, {
    params: {
      name: characterName,
      apikey: MARVEL_CONFIG.apiKey
    }
  });

  const characters = response.data.data.results || [];
  return characters.map(char => normalizeCharacter(char, 'marvel'));
}

async function searchSeriesMarvel(characterId = null, titleStartsWith = '', startYear = null, offset = 0) {
  let url = MARVEL_CONFIG.baseUrl;
  if (characterId) {
    url += `/characters/${characterId}`;
  }
  url += '/series';

  const params = {
    apikey: MARVEL_CONFIG.apiKey,
    orderBy: 'title',
    limit: 99,
    offset: offset
  };

  if (titleStartsWith) {
    params.titleStartsWith = titleStartsWith;
  }
  if (startYear) {
    params.startYear = startYear;
  }

  const response = await axios.get(url, { params });
  const series = response.data.data.results || [];
  const totalResults = response.data.data.total || series.length;
  const hasMore = (offset + series.length) < totalResults;

  return {
    results: series.map(s => normalizeSeries(s, 'marvel')),
    hasMore: hasMore,
    totalResults: totalResults,
    offset: offset
  };
}

async function getComicsForSeriesMarvel(seriesId, limit = 99, offset = 0) {
  const response = await axios.get(`${MARVEL_CONFIG.baseUrl}/series/${seriesId}/comics`, {
    params: {
      limit: limit,
      offset: offset,
      apikey: MARVEL_CONFIG.apiKey
    }
  });

  const comics = response.data.data.results || [];
  return comics.map(comic => normalizeComic(comic, 'marvel'));
}

// ============================================================================
// MOCK PROVIDER (For Testing)
// ============================================================================

async function searchCharacterMock(characterName, publisher = null) {
  // Return mock data for testing
  return [{
    id: `mock-${Date.now()}`,
    name: characterName,
    description: `Mock character: ${characterName}`,
    thumbnail: {
      path: 'https://via.placeholder.com/300x450',
      extension: 'png'
    },
    source: 'mock',
    publisher: publisher || 'unknown'
  }];
}

async function searchSeriesMock(characterId = null, titleStartsWith = '', startYear = null, offset = 0) {
  return {
    results: [{
      id: `mock-series-${Date.now()}`,
      title: titleStartsWith || 'Mock Series',
      startYear: startYear || 2020,
      endYear: 2024,
      description: 'Mock series for testing',
      thumbnail: {
        path: 'https://via.placeholder.com/300x450',
        extension: 'png'
      },
      comics: {
        available: 10
      }
    }],
    hasMore: false,
    totalResults: 1,
    offset: offset
  };
}

async function getComicsForSeriesMock(seriesId, limit = 99, offset = 0) {
  const comics = [];
  for (let i = 1; i <= Math.min(limit, 10); i++) {
    comics.push({
      id: `mock-comic-${seriesId}-${i + offset}`,
      title: `Mock Comic #${i + offset}`,
      issueNumber: i + offset,
      description: `Mock comic issue #${i + offset}`,
      thumbnail: {
        path: 'https://via.placeholder.com/300x450',
        extension: 'png'
      },
      series: {
        id: seriesId,
        name: 'Mock Series'
      }
    });
  }
  return comics;
}

// ============================================================================
// NORMALIZATION FUNCTIONS
// Convert different API formats to a common format
// ============================================================================

/**
 * Normalize character data from different providers
 */
function normalizeCharacter(data, source) {
  const now = new Date().toISOString();

  if (source === 'comicvine') {
    // Generate ID if not present (for story arcs and other resources)
    // Use Comic Vine ID if available, otherwise generate from name
    const generatedId = data.id || 
                       Math.abs(hashCode(data.name || 'unknown')) || 
                       Date.now();
    
    return {
      id: generatedId,
      name: data.name || 'Unknown',
      description: data.deck || data.description || 'Unknown',
      thumbnail: {
        path: data.image?.medium_url?.replace(/\.[^/.]+$/, '') || 'https://via.placeholder.com/300x450',
        extension: data.image?.medium_url?.split('.').pop() || 'jpg'
      },
      source: 'comicvine',
      publisher: data.publisher?.name || 'Unknown',
      comicVineId: generatedId,
      comicVineImage: data.image || null,
      createDate: now,
      modified: now,
      resourceURI: '',
      series: { available: 0, items: [] },
      stories: { available: 0, items: [] },
      urls: []
    };
  } else if (source === 'marvel') {
    return {
      id: data.id || Date.now(),
      name: data.name || 'Unknown',
      description: data.description || 'Unknown',
      thumbnail: data.thumbnail,
      source: 'marvel',
      publisher: data.publisher || 'Marvel',
      // Preserve legacy Marvel fields
      resourceURI: data.resourceURI || '',
      series: data.series || { available: 0, items: [] },
      stories: data.stories || { available: 0, items: [] },
      urls: data.urls || [],
      // Handle createDate and modified
      createDate: data.createDate || data.modified || now,
      modified: now
    };
  }
  return data;
}

/**
 * Simple hash function to generate numeric IDs from strings
 * Used when Comic Vine doesn't provide an ID (e.g., for some story arcs)
 */
function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
}

/**
 * Normalize series data from different providers
 */
function normalizeSeries(data, source) {
  if (source === 'comicvine') {
    return {
      id: data.id,
      title: data.name,
      startYear: data.start_year || new Date().getFullYear(),
      endYear: data.start_year || new Date().getFullYear(), // Comic Vine doesn't always have end year
      description: data.description || '',
      thumbnail: {
        path: data.image?.medium_url?.replace(/\.[^/.]+$/, '') || 'https://via.placeholder.com/300x450',
        extension: data.image?.medium_url?.split('.').pop() || 'jpg'
      },
      comics: {
        available: data.count_of_issues || 0
      },
      modified: new Date().toISOString(),
      publisher: data.publisher?.name || 'Unknown',
      comicVineId: data.id,
      comicVineImage: data.image || null
    };
  } else if (source === 'marvel') {
    return {
      id: data.id,
      title: data.title,
      startYear: data.startYear,
      endYear: data.endYear,
      description: data.description || '',
      thumbnail: data.thumbnail,
      comics: data.comics,
      modified: data.modified,
      publisher: 'Marvel'
    };
  }
  return data;
}

/**
 * Normalize comic data from different providers
 */
function normalizeComic(data, source) {
  if (source === 'comicvine') {
    return {
      id: data.id,
      title: data.name || `Issue #${data.issue_number}`,
      issueNumber: parseFloat(data.issue_number) || 0,
      description: data.description || '',
      dateAdded: data.date_added || null,
      coverDate: data.cover_date || null,
      storeDate: data.store_date || null,
      thumbnail: {
        path: data.image?.medium_url?.replace(/\.[^/.]+$/, '') || 'https://via.placeholder.com/300x450',
        extension: data.image?.medium_url?.split('.').pop() || 'jpg'
      },
      series: {
        id: data.volume?.id || 0,
        name: data.volume?.name || 'Unknown Series'
      },
      variantDescription: data.name?.includes('Variant') ? 'Variant' : null,
      comicVineId: data.id,
      comicVineImage: data.image || null
    };
  } else if (source === 'marvel') {
    const onSaleDate = data.dates?.find(d => d.type === 'onsaleDate')?.date || null;
    return {
      id: data.id,
      title: data.title,
      issueNumber: data.issueNumber,
      description: data.description || '',
      coverDate: onSaleDate,
      storeDate: onSaleDate,
      thumbnail: data.thumbnail,
      series: data.series,
      variantDescription: data.variantDescription
    };
  }
  return data;
}

// ============================================================================
// PUBLIC API
// Routes to the appropriate provider
// ============================================================================

/**
 * Search for a character across all sources
 * @param {string} characterName - Name of the character
 * @param {string} publisher - Optional publisher filter (marvel, dc, etc.)
 * @returns {Promise} Array of character results
 */
export async function searchCharacter(characterName, publisher = null) {
  switch (currentProvider) {
    case PROVIDERS.COMIC_VINE:
      return searchCharacterComicVine(characterName, publisher);
    case PROVIDERS.MARVEL:
      return searchCharacterMarvel(characterName, publisher);
    case PROVIDERS.MOCK:
      return searchCharacterMock(characterName, publisher);
    default:
      throw new Error(`Unknown provider: ${currentProvider}`);
  }
}

/**
 * Get a single comic series by its Comic Vine volume ID
 * @param {number|string} seriesId - Comic Vine volume ID
 * @returns {Promise} Normalized series object, or null if not found
 */
async function getSeriesByIdComicVine(seriesId) {
  // Validate that the ID is numeric to prevent injection via the filter string
  const numericId = parseInt(seriesId, 10);
  if (isNaN(numericId) || numericId <= 0) {
    throw new Error(`Invalid Comic Vine volume ID: "${seriesId}". ID must be a positive integer.`);
  }

  try {
    const params = {
      api_key: COMIC_VINE_CONFIG.apiKey,
      format: COMIC_VINE_CONFIG.format,
      filter: `id:${numericId}`,
      field_list: 'id,name,start_year,description,image,count_of_issues,publisher',
    };
    console.log(`Comic Vine volume lookup by id: ${numericId}`);
    const response = await makeComicVineRequest('/volumes', params);
    const results = response.data.results || [];
    if (results.length === 0) return null;
    return normalizeSeries(results[0], 'comicvine');
  } catch (error) {
    console.error('Comic Vine API error (getSeriesById):', error);
    throw error;
  }
}

/**
 * Get a single comic series by its provider-specific ID
 * @param {number|string} seriesId - Series/Volume ID
 * @returns {Promise} Normalized series object, or null if not found
 */
export async function getSeriesById(seriesId) {
  switch (currentProvider) {
    case PROVIDERS.COMIC_VINE:
      return getSeriesByIdComicVine(seriesId);
    default:
      throw new Error(`getSeriesById not supported for provider: ${currentProvider}`);
  }
}

/**
 * Fetch publisher names for a set of Comic Vine volume IDs.
 * Returns a Map<number, string> from volume ID to publisher name.
 * The Comic Vine /volumes endpoint supports filtering by multiple IDs
 * with a pipe-delimited list.
 * @param {number[]} volumeIds - Array of Comic Vine volume IDs
 * @returns {Promise<Map<number, string>>}
 */
export async function getVolumePublishers(volumeIds) {
  const publisherMap = new Map();
  if (!volumeIds || volumeIds.length === 0) return publisherMap;

  // Comic Vine filter supports pipe-delimited IDs; batch in groups of 50
  const batchSize = 50;
  for (let i = 0; i < volumeIds.length; i += batchSize) {
    const batch = volumeIds.slice(i, i + batchSize);
    try {
      const params = {
        api_key: COMIC_VINE_CONFIG.apiKey,
        format: COMIC_VINE_CONFIG.format,
        filter: `id:${batch.join('|')}`,
        field_list: 'id,publisher',
        limit: batchSize,
      };
      const response = await makeComicVineRequest('/volumes', params);
      const results = response.data.results || [];
      results.forEach(vol => {
        const pub = vol.publisher?.name;
        if (pub) {
          publisherMap.set(Number(vol.id), pub);
        }
      });
    } catch (err) {
      console.warn('getVolumePublishers batch failed:', err.message);
    }
  }
  return publisherMap;
}

/**
 * Search for comic series
 * @param {number} characterId - Optional character ID to filter by
 * @param {string} titleStartsWith - Series title search string
 * @param {number} startYear - Optional year filter
 * @param {number} offset - Offset for pagination (default 0)
 * @returns {Promise} Object with results array and pagination info
 */
export async function searchSeries(characterId = null, titleStartsWith = '', startYear = null, offset = 0) {
  switch (currentProvider) {
    case PROVIDERS.COMIC_VINE:
      return searchSeriesComicVine(characterId, titleStartsWith, startYear, offset);
    case PROVIDERS.MARVEL:
      return searchSeriesMarvel(characterId, titleStartsWith, startYear, offset);
    case PROVIDERS.MOCK:
      return searchSeriesMock(characterId, titleStartsWith, startYear, offset);
    default:
      throw new Error(`Unknown provider: ${currentProvider}`);
  }
}

/**
 * Get comics for a specific series
 * @param {number} seriesId - Series/Volume ID
 * @param {number} limit - Max results per page
 * @param {number} offset - Offset for pagination
 * @returns {Promise} Array of comic issues
 */
export async function getComicsForSeries(seriesId, limit = 99, offset = 0) {
  switch (currentProvider) {
    case PROVIDERS.COMIC_VINE:
      return getComicsForSeriesComicVine(seriesId, limit, offset);
    case PROVIDERS.MARVEL:
      return getComicsForSeriesMarvel(seriesId, limit, offset);
    case PROVIDERS.MOCK:
      return getComicsForSeriesMock(seriesId, limit, offset);
    default:
      throw new Error(`Unknown provider: ${currentProvider}`);
  }
}

/**
 * Search for recent comic series (Comic Vine only - uses date_added filter)
 * @param {string} titleStartsWith - Series title search string
 * @param {string} dateRange - Date range in format "YYYY-MM-DD|YYYY-MM-DD" (start|end)
 * @param {number} offset - Offset for pagination (default 0)
 * @returns {Promise} Object with results array and pagination info
 */
export async function searchRecentSeries(titleStartsWith = '', dateRange = null, offset = 0) {
  if (currentProvider === PROVIDERS.COMIC_VINE) {
    return searchRecentSeriesComicVine(titleStartsWith, dateRange, offset);
  } else {
    // Fallback to regular search for other providers
    return searchSeries(null, titleStartsWith, null, offset);
  }
}

/**
 * Search for a team across all sources
 * @param {string} teamName - Name of the team
 * @param {string} publisher - Optional publisher filter (marvel, dc, etc.)
 * @returns {Promise} Array of team results
 */
export async function searchTeam(teamName, publisher = null) {
  switch (currentProvider) {
    case PROVIDERS.COMIC_VINE:
      return searchTeamComicVine(teamName, publisher);
    case PROVIDERS.MARVEL:
    case PROVIDERS.MOCK:
      // Teams not supported for Marvel/Mock providers yet
      return [];
    default:
      throw new Error(`Unknown provider: ${currentProvider}`);
  }
}

/**
 * Search for a story arc across all sources
 * @param {string} arcName - Name of the story arc
 * @param {string} publisher - Optional publisher filter (marvel, dc, etc.)
 * @returns {Promise} Array of story arc results
 */
export async function searchStoryArc(arcName, publisher = null) {
  switch (currentProvider) {
    case PROVIDERS.COMIC_VINE:
      return searchStoryArcComicVine(arcName, publisher);
    case PROVIDERS.MARVEL:
    case PROVIDERS.MOCK:
      // Story arcs not supported for Marvel/Mock providers yet
      return [];
    default:
      throw new Error(`Unknown provider: ${currentProvider}`);
  }
}

/**
 * Get new comic releases for a date range using Comic Vine API
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {number} limit - Max results per page (default 100)
 * @param {number} offset - Offset for pagination (default 0)
 * @returns {Promise} Object with results array and pagination info
 */
async function getNewReleasesComicVine(startDate, endDate, limit = 100, offset = 0, dateField = 'cover') {
  try {
    let normalizedDateField = 'cover_date';
    if (dateField === 'store') {
      normalizedDateField = 'store_date';
    } else if (dateField === 'added') {
      normalizedDateField = 'date_added';
    }
    const params = {
      api_key: COMIC_VINE_CONFIG.apiKey,
      format: COMIC_VINE_CONFIG.format,
      filter: `${normalizedDateField}:${startDate}|${endDate}`,
      field_list: 'id,name,issue_number,description,image,volume,date_added,cover_date,store_date',
      sort: `${normalizedDateField}:desc`,
      limit: limit,
      offset: offset
    };

    console.log(`Comic Vine new releases (${normalizedDateField}): ${startDate} to ${endDate} (offset: ${offset})`);
    const response = await makeComicVineRequest('/issues', params);

    const issues = response.data.results || [];
    const totalResults = response.data.number_of_total_results || issues.length;
    const hasMore = (offset + issues.length) < totalResults;

    return {
      results: issues.map(comic => normalizeComic(comic, 'comicvine')),
      hasMore,
      totalResults,
      offset
    };
  } catch (error) {
    console.error('Comic Vine API error (getNewReleases):', error);
    throw error;
  }
}

/**
 * Get new comic releases for a date range
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {number} limit - Max results per page (default 100)
 * @param {number} offset - Offset for pagination (default 0)
 * @returns {Promise} Object with results array and pagination info
 */
export async function getNewReleases(startDate, endDate, limit = 100, offset = 0, options = {}) {
  const dateField = options.dateField === 'store'
    ? 'store'
    : options.dateField === 'added'
      ? 'added'
      : 'cover';
  if (currentProvider === PROVIDERS.COMIC_VINE) {
    return getNewReleasesComicVine(startDate, endDate, limit, offset, dateField);
  }
  // Return empty for unsupported providers
  return { results: [], hasMore: false, totalResults: 0, offset };
}

export { PROVIDERS };
